/**
 * P1-02 · Money utilities.
 *
 * All catalog money is integer **minor units** (pence, cents, …) + an ISO-4217
 * currency code — never Float/Decimal (P0-05 rule). These helpers are the only
 * sanctioned way to parse, format, and reason about money so rounding stays
 * deterministic and no floating-point drift reaches a price.
 *
 * Formatting/rounding is currency-aware via `Intl.NumberFormat` (GBP/USD → 2
 * digits, JPY → 0), so nothing here hard-codes "divide by 100".
 */

/**
 * P12-08 · ISO 4217 minor-unit exponents, pinned.
 *
 * This used to be read straight off `Intl` at call time, which made the scale of
 * every amount a property of whichever ICU build the process happened to load.
 * That is not hypothetical: CLDR disagrees with ISO 4217 about **HUF**, and
 * `Intl` reports 2 digits on Node 25 and 0 on Node 22 — and Node 22 is what
 * `.nvmrc` pins, so CI and production see 0 while a developer sees 2.
 *
 * Nine call sites depend on this number, including FX conversion
 * (`pricing/convert.ts`), price rounding, catalogue import, order display,
 * JSON-LD and CSV export. A silent ±100× between environments in any of those
 * is a money bug, and `MINIMUM_CHARGE_MINOR` below is already written in ISO
 * units (`HUF: 17_500` = 175 HUF), so the table and the runtime disagreed.
 *
 * Pinned values are ISO 4217, which is also what payment processors settle on.
 * Anything unlisted still falls back to `Intl` — better a locale-derived guess
 * than a hard failure for a currency nobody has priced in yet.
 */
const MINOR_UNIT_DIGITS: Record<string, number> = {
  AED: 2,
  AUD: 2,
  BGN: 2,
  CAD: 2,
  CHF: 2,
  CZK: 2,
  DKK: 2,
  EUR: 2,
  GBP: 2,
  HKD: 2,
  HUF: 2,
  ILS: 2,
  MXN: 2,
  MYR: 2,
  NOK: 2,
  NZD: 2,
  PLN: 2,
  RON: 2,
  SEK: 2,
  SGD: 2,
  THB: 2,
  TWD: 2,
  USD: 2,
  // Genuinely zero-decimal — ¥1,200 is 1200 minor units, not 120000.
  JPY: 0,
  KRW: 0,
  VND: 0,
  CLP: 0,
  ISK: 0,
};

/**
 * Number of minor-unit digits for a currency (GBP→2, JPY→0).
 *
 * `locale` is accepted for the `Intl` fallback only; a pinned currency resolves
 * identically in every locale, which is the entire point.
 */
export function currencyFractionDigits(
  currency: string,
  locale = "en",
): number {
  const pinned = MINOR_UNIT_DIGITS[currency.toUpperCase()];
  if (pinned !== undefined) return pinned;
  const fmt = new Intl.NumberFormat(locale, { style: "currency", currency });
  return fmt.resolvedOptions().maximumFractionDigits ?? 2;
}

/**
 * A major-unit amount from an external API (carrier quotes, FX feeds) → minor
 * units for this currency.
 *
 * Exists so integrations stop writing `Math.round(amount * 100)`, which is
 * wrong by two orders of magnitude for a zero-decimal currency: a ¥1,200
 * carrier quote became ¥120,000. Digits come from `Intl`, like everything else
 * here, so this agrees with `formatMoney` and `parseMoney` by construction.
 */
export function majorToMinor(amount: number, currency: string): number {
  return Math.round(amount * 10 ** currencyFractionDigits(currency));
}

/** Banker's rounding (round-half-to-even) of a possibly-fractional minor value. */
export function roundHalfEven(value: number): number {
  const floor = Math.floor(value);
  const diff = value - floor;
  // Guard against binary-float noise (e.g. 1999.0000000000002).
  const EPSILON = 1e-9;
  if (diff < 0.5 - EPSILON) return floor;
  if (diff > 0.5 + EPSILON) return floor + 1;
  return floor % 2 === 0 ? floor : floor + 1;
}

/**
 * Round-half-up of a possibly-fractional minor value (0.5 → 1). The canonical
 * rounding for the P4-14 tax engine: applied per-line so tax figures are
 * deterministic and identical across estimator, checkout, and invoice. Distinct
 * from `roundHalfEven` (used for aggregate price display) — tax law expects
 * half-up, and mixing strategies would drift a penny.
 */
export function roundHalfUp(value: number): number {
  const EPSILON = 1e-9;
  return Math.floor(value + 0.5 + EPSILON);
}

/**
 * Parse a major-unit amount ("24.00", "£24", 24) into integer minor units for
 * the given currency, using half-even rounding. Throws on non-numeric input.
 */
export function parseMoney(
  input: string | number,
  currency: string,
  locale = "en",
): number {
  const digits = currencyFractionDigits(currency, locale);
  let value: number;
  if (typeof input === "number") {
    value = input;
  } else {
    // Strip currency symbols/grouping, but require at least one digit — a strip
    // to "" would otherwise coerce to 0 and hide invalid input.
    const cleaned = input.replace(/[^0-9.-]/g, "");
    value = /\d/.test(cleaned) ? Number(cleaned) : NaN;
  }
  if (!Number.isFinite(value)) {
    throw new Error(`Cannot parse money from ${JSON.stringify(input)}`);
  }
  return roundHalfEven(value * 10 ** digits);
}

/**
 * P15-07 · A TYPED amount from an operator → integer minor units, or null if it
 * isn't a usable positive amount yet.
 *
 * `parseMoney` is deliberately lenient — it strips symbols and grouping so it
 * can read a price out of anything — which is right for parsing and wrong for a
 * field where a human is about to move money. "1-2" quietly becoming 1 is the
 * kind of leniency a goodwill amount must not have, so the shape is checked
 * before parsing and anything but a plain positive decimal is refused.
 *
 * Returns null rather than throwing: it runs on every keystroke, and "not a
 * number yet" is the normal state of a field being typed into.
 *
 * Extracted from P13-03's store-credit form, which is now one of its callers —
 * the two surfaces that let staff create value should not disagree about what
 * "10" means.
 */
export function parseOperatorAmount(
  input: string,
  currency: string | undefined,
): number | null {
  const trimmed = input.trim();
  if (!trimmed || !currency) return null;
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return null;
  try {
    const minor = parseMoney(trimmed, currency);
    return minor > 0 ? minor : null;
  } catch {
    return null;
  }
}

/**
 * Format integer minor units as a localised currency string, e.g.
 * `formatMoney(2400, "GBP", "en-GB") === "£24.00"`.
 */
export function formatMoney(
  minor: number,
  currency: string,
  locale = "en-GB",
): string {
  const fmt = new Intl.NumberFormat(locale, { style: "currency", currency });
  // The DIVISOR is the pinned scale, not the locale's. Reading it off `Intl`
  // here meant a HUF amount stored as 17500 minor rendered as "17,500 Ft" on a
  // runtime that thinks HUF has no minor unit, and "175 Ft" on one that does —
  // the same row, off by 100×, depending on the container. How many decimals to
  // *show* is still the locale's business, which is why `fmt` is unchanged.
  const digits = currencyFractionDigits(currency, locale);
  return fmt.format(minor / 10 ** digits);
}

/** The currency's symbol (e.g. "£", "$") for compact labels like filter chips. */
/**
 * P8-05B · The smallest amount a card network will actually accept, per
 * currency, in minor units.
 *
 * Card processors reject charges below a floor (Stripe: 30p, €0.50, ¥50, and a
 * long tail of much larger ones for currencies with weak minor units). Below it
 * there is no such thing as a successful small charge — the request simply
 * fails.
 *
 * That matters because gift-card tender can leave a residual smaller than the
 * floor. `computeCartSummary` absorbs those rather than building a charge that
 * cannot succeed; see `minimumChargeMinor` there for why it only ever applies
 * to a post-tender remainder.
 *
 * Deliberately provider-agnostic and slightly conservative: it is applied at
 * projection time, before the shopper has picked a tender, and offline tenders
 * (COD, bank transfer) have no floor of their own — waiving a 29-cent cash
 * collection is a better outcome than demanding it.
 */
const MINIMUM_CHARGE_MINOR: Record<string, number> = {
  GBP: 30,
  USD: 50,
  EUR: 50,
  JPY: 50,
  AUD: 50,
  CAD: 50,
  CHF: 50,
  NZD: 50,
  SGD: 50,
  BGN: 100,
  DKK: 250,
  NOK: 300,
  SEK: 300,
  HKD: 400,
  PLN: 200,
  RON: 200,
  AED: 200,
  ILS: 200,
  MYR: 200,
  TWD: 700,
  MXN: 1000,
  THB: 1000,
  CZK: 1500,
  HUF: 17_500,
};

export function minimumChargeMinor(currency: string): number {
  // 50 minor units is the common floor and a safe default for an unlisted
  // currency: high enough to catch the residuals this exists for, low enough
  // that it cannot absorb a real order.
  return MINIMUM_CHARGE_MINOR[currency.toUpperCase()] ?? 50;
}

export function currencySymbol(currency: string, locale = "en-GB"): string {
  const parts = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).formatToParts(0);
  return parts.find((p) => p.type === "currency")?.value ?? currency;
}

/** True when a compare-at (was-) price is set and strictly above the price. */
export function isOnSale(
  priceMinor: number,
  compareAtMinor: number | null | undefined,
): boolean {
  return compareAtMinor != null && compareAtMinor > priceMinor;
}

/**
 * Whole-number discount percent off the compare-at price, or `null` when not on
 * sale. `discountPercent(1800, 2400) === 25`.
 */
export function discountPercent(
  priceMinor: number,
  compareAtMinor: number | null | undefined,
): number | null {
  if (!isOnSale(priceMinor, compareAtMinor)) return null;
  return Math.round(((compareAtMinor! - priceMinor) / compareAtMinor!) * 100);
}
